<template>
  <div>
    <v-row>
      <v-col cols="12">
        <validation-provider v-slot="{ errors }" rules="required">
          <v-text-field
            id="titulo-movimentacao-blindada"
            v-model="titulo"
            class="my-0"
            label="Título*"
            outlined
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="my-0 pt-0">
        <v-alert
          dense
          outlined
          type="info"
          class="mb-0 py-0 px-1"
        >
          IPCA oficial atualizados até: <b>{{ dataUltimoIpcaOficial | formatToMonth }}</b> ({{ mesesSemAtualizarIpcaOficial }} meses sem atualizar)<br>
          <template v-if="dataUltimoIpcaNaoOficial">IPCA estimado atualizados até: <b>{{ dataUltimoIpcaNaoOficial | formatToMonth }}</b></template>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4" id="date-picker-data-corte-ultimo-ciclo">
        <date-picker
          v-model="anoMesCorteUltimoCiclo"
          label="Data de Corte do Último Ciclo*"
          type="month"
          rules="required|min:10"
          hint="Informe uma data base de um laudo blindado importado"
          persistent-hint
          outlined
          @change="carregarVersoesDisponiveis()"
        />
      </v-col>
      <v-col cols="12" md="4" id="select-versao-movimentacao-blindada">
        <validation-provider v-slot="{ errors }">
          <v-select
            v-model="versao"
            :items="versoesDisponiveisLocal"
            item-text="text"
            item-value="value"
            :loading="versoesLoadingLocal"
            :disabled="!anoMesCorteUltimoCiclo"
            :no-data-text="anoMesCorteUltimoCiclo ? 'Nenhuma versão disponível para a data selecionada' : 'Selecione a data de corte primeiro'"
            label="Versão do Laudo"
            placeholder="Selecione a versão, se quiser"
            clearable
            outlined
            persistent-hint
            hint="Opcional. Se não selecionar, o processamento seguirá o comportamento legado por data de corte"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col cols="12" md="4" id="date-picker-data-atualizacao">
        <date-picker
          v-model="atualizarAte"
          label="Atualizar Até*"
          type="month"
          :rules="atualizarAte && dataAtualizarAteValida ? 'required' : 'required|forcar_erro'"
          outlined
        />
      </v-col>
    </v-row>
    <v-divider class="mb-4" />
    <v-expansion-panels v-model="panel" multiple>
      <v-expansion-panel
        v-for="config in arquivosConfig"
        :key="config.key"
      >
        <v-expansion-panel-header :id="config.id">
          <label style="font-size: 15px !important">{{ config.label }}</label>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <v-row>
            <v-col cols="12">
              <v-alert dense outlined type="info" color="blue">
                <div>
                  <a href="javascript:void(0)" @click="getArquivoModelo(config)">Clique aqui</a> para baixar o modelo da importação.
                  <br>OBS: O arquivo deve conter as seguintes colunas: <b>{{ config.headers.join(', ') }}</b> (exatamente nessa ordem e com esses nomes).
                  <br>OBS²: O arquivo enviado precisa estar no formato <b>UTF-8</b>.
                  <br v-if="config.optional">
                  <span v-if="config.optional"><b>Opcional:</b> se não enviar este arquivo o sistema utilizará o valor de origem da base.</span>
                </div>
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <validation-provider v-slot="{ errors }" :rules="config.required ? 'required' : ''">
                <v-file-input
                  :value="getArquivoState(config.key).fileObj"
                  :label="config.label"
                  placeholder="Selecione um arquivo CSV"
                  outlined
                  accept=".csv, .zip"
                  truncate-length="30"
                  :loading="getArquivoState(config.key).uploading"
                  loader-height="4"
                  :success="!!(getArquivoState(config.key).id && getArquivoState(config.key).fileObj)"
                  :error-messages="errors"
                  @change="uploadNewFile(config.key, $event)"
                  @click:clear="clearArquivo(config.key)"
                />
              </validation-provider>
              <validation-provider v-if="config.required" rules="required">
                <input
                  type="hidden"
                  :name="`validate-upload-${config.key}`"
                  :value="getArquivoState(config.key).id"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row class="mx-auto mt-n2 mb-n5">
            <v-col cols="12" class="pl-n1 pt-0">
              <parametros-arquivo-csv
                :delimitador="getArquivoState(config.key).delimitador"
                :involucro="getArquivoState(config.key).involucro"
                :escape="getArquivoState(config.key).escape"
                @update:delimitador="updateArquivoField(config.key, 'delimitador', $event)"
                @update:involucro="updateArquivoField(config.key, 'involucro', $event)"
                @update:escape="updateArquivoField(config.key, 'escape', $event)"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import AtualizacaoIndicesService from '@/services/AtualizacaoIndicesService';
import ArquivosService from "@/services/ArquivosService";
import MovimentacaoBlindadaService from '@/services/MovimentacaoBlindadaService';

export default {
  components: {
    DatePicker:           () => import("@/components/general/DatePicker.vue"),
    ParametrosArquivoCsv: () => import("@/components/general/GeneralParametrosArquivoCsv.vue")
  },

  created() {
    this.getUltimoIndice();
  },

  mounted() {
    this.abrirPainel();
  },

  data: () => ({
    dataUltimoIpcaOficial: null,
    dataUltimoIpcaNaoOficial: null,
    dataAtualizarAteValida: true,
    versoesDisponiveisLocal: [],
    versoesLoadingLocal: false,
    panel: [],
    arquivosConfig: [
      {
        key: 'arquivoBaixas',
        modelType: 'baixas',
        id: 'arquivo-baixas-movimentacao-blindada',
        label: 'Arquivo de Baixas',
        required: true,
        optional: false,
        headers: ['Imobilizado', 'Classe', 'Centro custo', 'Denominacao', 'Nro documento', 'Dt lancamento', 'Quantidade', 'Unid med', 'Valor Aquisicao', 'Ajuste Valor', 'Depreciacao Movimento', 'Tipo Baixa']
      },
      {
        key: 'arquivoTransferencias',
        modelType: 'transferencias',
        id: 'arquivo-transferencias-movimentacao-blindada',
        label: 'Arquivo de Transferências',
        required: true,
        optional: false,
        headers: ['Nro documento', 'Imobilizado', 'Para.Imobilizado', 'Dt lancamento']
      },
      {
        key: 'arquivoIa',
        modelType: 'ia',
        id: 'arquivo-ia-movimentacao-blindada',
        label: 'Arquivo de IA',
        required: false,
        optional: true,
        headers: ['CD_PAT_LAUDO', 'CD_DIG_INC', '%IA']
      },
      {
        key: 'arquivoElegibilidade',
        modelType: 'elegibilidade',
        id: 'arquivo-elegibilidade-movimentacao-blindada',
        label: 'Arquivo de Elegibilidade',
        required: false,
        optional: true,
        headers: ['CD_PAT_LAUDO', 'CD_DIG_INC', 'ELEGIBILIDADE']
      }
    ],
    mesesSemAtualizarIpcaOficial: 0
  }),

  computed: {
    titulo: {
      get () { return this.$store.state.movimentacaoBlindada.titulo},
      set (value) { this.$store.dispatch('setMovimentacaoBlindada', {'titulo': value})}
    },
    anoMesCorteUltimoCiclo: {
      get () { return this.$store.state.movimentacaoBlindada.anoMesCorteUltimoCiclo},
      set (value) { this.$store.dispatch('setMovimentacaoBlindada', {'anoMesCorteUltimoCiclo': value})}
    },
    versao: {
      get () { return this.$store.state.movimentacaoBlindada.versao},
      set (value) { this.$store.dispatch('setMovimentacaoBlindada', {'versao': value})}
    },
    atualizarAte: {
      get () { return this.$store.state.movimentacaoBlindada.atualizarAte},
      set (value) { this.$store.dispatch('setMovimentacaoBlindada', {'atualizarAte': value})}
    }
  },

  methods:{
    getArquivoState(key) {
      return this.$store.state.movimentacaoBlindada[key];
    },

    updateArquivoField(key, field, value) {
      this.$store.dispatch('setMovimentacaoBlindada', { [key]: { [field]: value }});
    },

    exportData() {
      const vuexData = this.$store.state.movimentacaoBlindada;

      return {
        id: this.$route.params.id ? this.$route.params.id : null,
        titulo: vuexData.titulo,
        ano_mes_corte_ultimo_ciclo: vuexData.anoMesCorteUltimoCiclo,
        versao: vuexData.versao,
        atualizar_ate: vuexData.atualizarAte,
        arquivo_baixas_id: vuexData.arquivoBaixas.id,
        arquivo_baixas_delimitador: vuexData.arquivoBaixas.delimitador,
        arquivo_baixas_involucro: vuexData.arquivoBaixas.involucro,
        arquivo_baixas_escape: vuexData.arquivoBaixas.escape,
        arquivo_transferencias_id: vuexData.arquivoTransferencias.id,
        arquivo_transferencias_delimitador: vuexData.arquivoTransferencias.delimitador,
        arquivo_transferencias_involucro: vuexData.arquivoTransferencias.involucro,
        arquivo_transferencias_escape: vuexData.arquivoTransferencias.escape,
        arquivo_ia_id: vuexData.arquivoIa.id,
        arquivo_ia_delimitador: vuexData.arquivoIa.delimitador,
        arquivo_ia_involucro: vuexData.arquivoIa.involucro,
        arquivo_ia_escape: vuexData.arquivoIa.escape,
        arquivo_elegibilidade_id: vuexData.arquivoElegibilidade.id,
        arquivo_elegibilidade_delimitador: vuexData.arquivoElegibilidade.delimitador,
        arquivo_elegibilidade_involucro: vuexData.arquivoElegibilidade.involucro,
        arquivo_elegibilidade_escape: vuexData.arquivoElegibilidade.escape
      };
    },

    getUltimoIndice() {
      AtualizacaoIndicesService.getUltimoIndiceIpca().then((indice) => {
        this.dataUltimoIpcaOficial = indice.indice_oficial ? indice.indice_oficial.DataRegistro : '';
        this.dataUltimoIpcaNaoOficial = indice.indice_estimado ? indice.indice_estimado.DataRegistro : '';

        let dataAtual = dayjs();
        let qtdMeses = dataAtual.diff((dayjs(this.dataUltimoIpcaOficial)), 'month');
        this.mesesSemAtualizarIpcaOficial = qtdMeses;
      })
      .catch(() => this.$toast.error('Erro ao buscar ultimo Índice IPCA.', '', { position: 'topRight' }));
    },

    formatarLabelVersao(versao) {
      if (!versao) return '-';
      return /^v/i.test(versao) ? versao : `v${versao}`;
    },

    carregarVersoesDisponiveis() {
      if (!this.anoMesCorteUltimoCiclo) {
        this.versoesDisponiveisLocal = [];
        this.versao = null;
        return;
      }

      this.versoesLoadingLocal = true;

      MovimentacaoBlindadaService.getVersoesByData(this.anoMesCorteUltimoCiclo)
        .then(response => {
          const versoes = (response.data.data || []).map(versao => ({
            text: this.formatarLabelVersao(versao),
            value: versao
          }));
          this.versoesDisponiveisLocal = versoes;

          if (this.versao && !versoes.some(item => item.value === this.versao)) {
            this.versao = null;
          }
        })
        .catch(error => {
          this.versoesDisponiveisLocal = [];
          this.versao = null;
          this.$toast.error('Erro ao buscar versões do laudo blindado.', '', { position: 'topRight' });
          console.error(error);
        })
        .finally(() => {
          this.versoesLoadingLocal = false;
        });
    },

    validarDataContempladaIPCA(data) {
      if (!data) return false;

      const dataReferencia = dayjs(data);
      const dataIpca = this.dataUltimoIpcaNaoOficial || this.dataUltimoIpcaOficial;
      if (dataReferencia.isAfter(dataIpca)) {
        this.$toast.error('Erro: A data de referência está incorreta, confira o ultimo IPCA .', '', { position: 'topRight' });
        return false;
      }

      return true;
    },

    getArquivoModelo(config) {
      this.$toast.info("Processando...", "", { position: "topRight", timeout: 1250 });

      MovimentacaoBlindadaService.getArquivoModelo(config.modelType)
        .then(async res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `arquivo_modelo_movimentacao_blindada_${config.modelType}.csv`);
          document.body.appendChild(link);
          link.click();
        })
        .catch(err => {
          this.$toast.error("Erro ao baixar arquivo modelo", "", { position: "topRight"});
          console.error(err);
        })
    },

    uploadNewFile(key, arquivo) {
      if (!arquivo) {
        this.clearArquivo(key);
        return true;
      }

      this.updateArquivoField(key, 'fakeFile', false);
      this.updateArquivoField(key, 'id', "");
      this.updateArquivoField(key, 'uploading', true);
      this.updateArquivoField(key, 'fileObj', arquivo);

      let formDataWithFile = new FormData();
      formDataWithFile.append("file", arquivo);

      ArquivosService.uploadFile(formDataWithFile)
        .then(async response => {
          const state = this.getArquivoState(key);
          const config = this.arquivosConfig.find(item => item.key === key);

          await this.validaHeader(
            response.data.arquivoId,
            state.delimitador,
            state.involucro,
            state.escape,
            config.headers
          );

          this.updateArquivoField(key, 'id', response.data.arquivoId);
          this.updateArquivoField(key, 'headerValido', true);
        })
        .catch((err) => {
          if (err?.data?.error_code == 120 || err?.data?.error_code == 121) {
            this.$toast.error(err.data.erro, "", { position: "topRight", timeout: 6000 });
          } else {
            this.$toast.error("Erro no upload do arquivo.", "", { position: "topRight" });
            console.error("Erro ao fazer upload: ", err);
          }

          this.clearArquivo(key);
        })
        .finally(() => this.updateArquivoField(key, 'uploading', false));
    },

    validaHeader(arquivoId, delimitador, involucro, escape, cabecalhoEsperado) {
      return ArquivosService.validarHeaderCSV(arquivoId, { delimitador, involucro, escape, cabecalhoEsperado })
        .catch(err => { throw err });
    },

    clearArquivo(key) {
      this.$store.dispatch('setMovimentacaoBlindada', {
        [key]: {
          id: "",
          fileObj: null,
          delimitador: ";",
          involucro: `"`,
          escape: `"`,
          uploading: false,
          fakeFile: false,
          headerValido: false
        }
      });

      this.$forceUpdate();
    },

    abrirPainel() {
      this.panel = this.arquivosConfig.map((_, index) => index);
    }
  },

  watch: {
    anoMesCorteUltimoCiclo() {
      this.carregarVersoesDisponiveis();
    },
    atualizarAte(newDate) {
      let dataValida = this.validarDataContempladaIPCA(newDate);
      this.dataAtualizarAteValida = dataValida;
    }
  }
}
</script>
